import { GitBranchIcon, ListTreeIcon } from "lucide-react";
import type { CmmdBoardTask } from "@cmmd-forge/contracts";
import type { SidebarThreadSummary } from "~/types";
import { formatWorkItemId } from "~/lib/formatWorkItemId";
import { cn } from "~/lib/utils";
import { PriorityIcon } from "~/components/projects/ProjectsPriorityIcon";
import { deriveTaskPresentation, type TaskPresentation } from "./taskPresentation";
import {
  deriveSubtaskProgress,
  type SubtaskProgress as SubtaskProgressModel,
} from "./subtaskProgress";
import { AgentStatusBadge } from "./atoms/AgentStatusBadge";
import { SubtaskProgress } from "./atoms/SubtaskProgress";
import { TaskCardOwnership } from "./atoms/TaskCardOwnership";
import { TaskWorkItemTypeIcon } from "./atoms/TaskWorkItemTypeIcon";

export interface ProjectTaskCardProps {
  readonly task: CmmdBoardTask;
  readonly threadIndex: ReadonlyMap<string, SidebarThreadSummary>;
  readonly pending: boolean;
  readonly hasConversation: boolean;
  readonly onOpenTask?: (() => void) | undefined;
  readonly onOpenThread?: ((taskId: number) => void) | undefined;
  readonly workItemPrefix?: string | null | undefined;
}

function TaskCardSubtaskChip({
  progress,
  subtaskCount,
}: {
  readonly progress: SubtaskProgressModel | null;
  readonly subtaskCount: number;
}) {
  if (progress != null) {
    return (
      <span data-task-card-subtasks className="inline-flex">
        <SubtaskProgress progress={progress} showLabel={false} />
      </span>
    );
  }
  if (subtaskCount > 0) {
    return (
      <span
        data-task-card-subtasks
        className="inline-flex items-center gap-1 text-meta text-muted-foreground md:text-[11px]"
      >
        <ListTreeIcon className="size-3" aria-hidden />
        {subtaskCount}
      </span>
    );
  }
  return null;
}

function TaskCardThreadChip({
  presentation,
  hasHistoryChip,
  taskId,
  onOpenThread,
}: {
  readonly presentation: TaskPresentation;
  readonly hasHistoryChip: boolean;
  readonly taskId: number;
  readonly onOpenThread?: ((taskId: number) => void) | undefined;
}) {
  if (presentation.resolvedThread) {
    return (
      <AgentStatusBadge
        agentStatus={presentation.resolvedThread.agentStatus}
        agentProvider={presentation.resolvedThread.agentProvider}
        onClick={onOpenThread ? () => onOpenThread(taskId) : undefined}
        className="shrink-0"
      />
    );
  }
  if (hasHistoryChip) {
    return (
      <span className="inline-flex items-center gap-1 text-meta text-muted-foreground md:text-[11px]">
        <GitBranchIcon className="size-3" aria-hidden />
        History
      </span>
    );
  }
  return null;
}

function TaskCardMeta({
  presentation,
  hasHistoryChip,
  taskId,
  onOpenThread,
}: {
  readonly presentation: TaskPresentation;
  readonly hasHistoryChip: boolean;
  readonly taskId: number;
  readonly onOpenThread?: ((taskId: number) => void) | undefined;
}) {
  const hasMeta = presentation.resolvedThread != null || hasHistoryChip;
  if (!hasMeta) return null;

  return (
    <div className="mt-1.5 flex min-w-0 flex-wrap items-center gap-1.5">
      <TaskCardThreadChip
        presentation={presentation}
        hasHistoryChip={hasHistoryChip}
        taskId={taskId}
        onOpenThread={onOpenThread}
      />
    </div>
  );
}

/**
 * Kanban card face for ProjectTaskBoardView. DataBoard owns drag + click-to-open.
 * Status changes via column placement / DnD — no status glyph on the card face.
 *
 * Corner layout: title + priority/subtasks (top-right) → activity → plain ID (bottom-left)
 * · blocked + member (bottom-right). No Created footer.
 */
export function ProjectTaskCard({
  task,
  threadIndex,
  pending,
  hasConversation,
  onOpenTask,
  onOpenThread,
  workItemPrefix,
}: ProjectTaskCardProps) {
  const presentation = deriveTaskPresentation(task, threadIndex);
  const workItemId = formatWorkItemId(workItemPrefix, task.id);
  const progress = deriveSubtaskProgress(task);
  const hasHistoryChip = hasConversation || task.threadId !== null;

  return (
    <div
      data-task-card
      className={cn(
        "cursor-pointer rounded-lg border border-border p-2.5 text-left transition-colors duration-150",
        "bg-[var(--task-board-card)] hover:bg-[var(--task-board-hover)]",
        "focus-within:ring-2 focus-within:ring-ring",
        pending ? "opacity-60" : null,
      )}
    >
      <div className="flex items-start gap-2">
        {onOpenTask != null ? (
          <p
            role="button"
            tabIndex={0}
            onClick={(event) => {
              event.stopPropagation();
              onOpenTask();
            }}
            onKeyDown={(event) => {
              if (event.key !== "Enter" && event.key !== " ") return;
              event.preventDefault();
              event.stopPropagation();
              onOpenTask();
            }}
            aria-label={`Open task ${workItemId}: ${task.title}`}
            className="min-h-touch min-w-0 flex-1 whitespace-normal break-words rounded-sm text-left text-read font-semibold leading-5 text-foreground focus-visible:outline-none md:min-h-0 md:text-sm"
          >
            {task.title}
          </p>
        ) : (
          <p className="min-w-0 flex-1 whitespace-normal break-words text-read font-semibold leading-5 text-foreground md:text-sm">
            {task.title}
          </p>
        )}
        <div
          data-task-card-corner-meta
          className="flex shrink-0 flex-col items-center gap-1 pt-0.5"
        >
          <PriorityIcon priority={task.priority} />
          <TaskCardSubtaskChip progress={progress} subtaskCount={presentation.subtaskCount} />
        </div>
      </div>

      <TaskCardMeta
        presentation={presentation}
        hasHistoryChip={hasHistoryChip}
        taskId={task.id}
        onOpenThread={onOpenThread}
      />

      <div className="mt-1.5 flex items-center gap-2">
        <span className="inline-flex min-w-0 items-center gap-1.5 text-muted-foreground">
          <TaskWorkItemTypeIcon workItemType={task.workItemType} />
          <span
            data-task-card-work-item-id
            className="max-w-full truncate font-mono text-meta tabular-nums text-muted-foreground md:text-[11px]"
          >
            {workItemId}
          </span>
        </span>
        <span
          data-task-card-footer-meta
          className="ml-auto inline-flex shrink-0 items-center gap-1.5"
        >
          <TaskCardOwnership task={task} />
        </span>
      </div>
    </div>
  );
}
